<template>
  <div>
    <div class="container pt-125 md:pt-175 lg:pt-200">
      <breadcrumb
        :breadcrumbs="[
          { uri: category?.uri.split('/')[0], title: category?.uri.split('/')[0] },
          category,
          data?.product,
        ].filter((b) => b)"
      />
    </div>

    <section v-if="data?.product">
      <div class="container">
        <div class="grid lg:grid-cols-3">
          <div class="lg:col-span-1">
            <productInfo
              class="lg:text-lg"
              :product="data.product"
            />
          </div>

          <div class="lg:col-span-2 lg:row-span-3">
            <lightgallery
              :settings="lightGallery"
              class="flex flex-wrap items-start"
            >
              <div
                :class="{ 'sm:w-3/4 sm:pr-30': data.product.images?.length > 1 }"
                class="w-full lg:pl-30"
              >
                <a
                  v-if="data.product.mainImage?.length"
                  :href="data.product.mainImage[0].original_url"
                  aria-label="image"
                  class="block mt-30 aspect-[131/200] bg-primary-100 hover:bg-primary-200 transition"
                >
                  <img
                    :src="data.product.mainImage[0].url"
                    :srcset="data.product.mainImage[0].srcset"
                    sizes="(max-width: 600px) 100px, 100vw"
                    :alt="data.product.title"
                    width="683"
                    height="1043"
                    class="object-contain object-center w-full h-full mix-blend-darken"
                  >
                </a>
                <img
                  v-else
                  src="@/assets/img/placeholder-lg.png"
                  :alt="data.product.title"
                  width="683"
                  height="1043"
                  class="block mt-30 bg-primary-100 hover:bg-primary-200 transition mix-blend-darken"
                >
              </div>
              <ul
                v-if="data.product.images?.length > 1"
                class="w-full sm:w-1/4 flex sm:flex-wrap items-start space-x-15 sm:space-x-0"
              >
                <li
                  v-for="image in data.product.images.slice(1)"
                  :key="`product-image-${image.id}`"
                  class="w-1/3 sm:w-full"
                >
                  <a
                    :href="image.original_url"
                    aria-label="image"
                    class="block relative bg-primary-100 mt-15 sm:mt-30 hover:bg-primary-200 transition"
                  >
                    <img
                      :src="image.url"
                      :srcset="image.srcset"
                      sizes="(max-width: 600px) 50px, 100vw"
                      :alt="data?.product?.title"
                      class="mix-blend-darken"
                      width="250"
                      height="250"
                      loading="lazy"
                    >
                  </a>
                </li>
              </ul>
            </lightgallery>
          </div>

          <div class="lg:col-span-1">
            <productBuy
              :product="data.product"
              :interior-advice="data.interiorAdvice"
              :global-data="data.requestQuote"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="my-100">
      <div class="container leading-loose">
        <div class="flex flex-wrap md:-mx-25">
          <div class="w-full lg:w-1/3 md:px-25 mt-50 lg:mt-0">
            <h5 class="font-serif font-bold text-xl mb-10">
              {{ $t('product.description') }}
            </h5>
            <div
              class="wysiwyg"
              v-html="data?.product?.logicTradeDescription"
            />
          </div>
          <div class="w-full md:w-1/2 lg:w-1/3 md:px-25 mt-50 lg:mt-0">
            <h5 class="font-serif font-bold text-xl mb-10">
              {{ $t('product.details') }}
            </h5>
            <table>
              <tbody>
                <tr v-if="data?.product?.logicTradeBrand.length">
                  <td>{{ $t('product.brand') }}</td>
                  <td>
                    <NuxtLink
                      :to="`/${data.product.logicTradeBrand[0].uri}`"
                      class="underline hover:no-underline"
                    >
                      {{ data.product.logicTradeBrand[0].title }}
                    </NuxtLink>
                  </td>
                </tr>
                <tr
                  v-for="detail in data.product.logicTradeDetails"
                  :key="`detail-${detail.attribute}`"
                >
                  <td>{{ detail.attribute }}</td>
                  <td>
                    <template v-if="detail.value === 'True'">
                      {{ $t('general.yes') }}
                    </template>
                    <template v-else-if="detail.value === 'False'">
                      {{ $t('general.no') }}
                    </template>
                    <template v-else>
                      {{ $t(detail.value) }}
                    </template>
                  </td>
                </tr>
                <tr v-if="data?.product?.variants.length">
                  <td>{{ $t('product.sku') }}</td>
                  <td>{{ data.product?.variants[0].sku }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="data?.product?.productTypeHandle !== 'rugs'"
            class="w-full md:w-1/2 lg:w-1/3 md:px-25 mt-50 lg:mt-0"
          >
            <h5 class="font-serif font-bold text-xl mb-10">
              {{ $t('product.dimensions') }}
            </h5>
            <table>
              <tbody>
                <template v-if="data?.product?.variants.length">
                  <tr v-if="data.product.variants[0].height">
                    <td>{{ $t('product.height') }}</td>
                    <td>{{ data.product.variants[0].height }} cm</td>
                  </tr>
                  <tr v-if="data.product.variants[0].width">
                    <td>{{ $t('product.width') }}</td>
                    <td>{{ data.product.variants[0].width }} cm</td>
                  </tr>
                  <tr v-if="data.product.variants[0].length">
                    <td>{{ $t('product.depth') }}</td>
                    <td>{{ data.product.variants[0].length }} cm</td>
                  </tr>
                  <tr v-if="data.product.variants[0].weight">
                    <td>{{ $t('product.weight') }}</td>
                    <td>{{ data.product.variants[0].weight }} kg</td>
                  </tr>
                </template>
                <tr
                  v-for="size in data.product?.logicTradeSizes"
                  :key="`size-${size.attribute}`"
                >
                  <td>{{ size.attribute }}</td>
                  <td>{{ size.value }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-if="data.product?.heading || data.product?.productUsps?.length > 1"
          class="bg-primary-100 mt-50 p-25 md:p-50"
        >
          <div class="flex flex-wrap">
            <div
              v-if="data.product?.heading"
              class="w-full lg:w-1/3 md:pr-50"
            >
              <h3
                class="wysiwyg-title text-3xl lg:text-4xl xl:text-5xl leading-tight"
                v-html="data.product?.heading"
              />
              <div class="flex space-x-25 items-center mt-25">
                <img
                  v-if="data.product?.image?.length"
                  :src="data.product?.image[0].url"
                  alt=""
                  class="w-[85px] rounded-full"
                  loading="lazy"
                >
                <p
                  class="wysiwyg-title leading-tight text-lg"
                  v-html="data.product?.heading2"
                />
              </div>
            </div>
            <div class="w-full lg:w-2/3 mt-50 lg:mt-0 text-lg leading-normal">
              <ul
                v-if="data.product?.productUsps?.length > 1"
                class="space-y-20"
              >
                <li
                  v-for="(usp, index) in data.product.productUsps"
                  :key="`usp-${index}`"
                  class="flex items-start space-x-10"
                >
                  <circlePlusIcon
                    v-if="usp.type === 'plus'"
                    class="flex-shrink-0"
                  />
                  <circleMinIcon
                    v-else
                    class="flex-shrink-0 text-primary-300"
                  />
                  <span class="mt-1">{{ usp.text }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <content-blocks :content-blocks="data?.product?.contentBlocks" />
  </div>
</template>

<script setup>
import Lightgallery from 'lightgallery/vue/LightGalleryVue.common.js';
import {
  ref,
  useRoute,
  useI18n,
  useAsyncGql,
  watch,
  useSeo,
  onMounted,
  useNuxtApp,
  useDataLayer,
  computed,
  createError,
} from '#imports';

// General icons
import circleMinIcon from '@/assets/img/icon-circle-min.svg';
import circlePlusIcon from '@/assets/img/icon-circle-plus.svg';

// Data
const route = useRoute();
const { locale } = useI18n({ useScope: 'global' });
const variables = ref({
  site: locale.value,
  uri: route.path.substring(1).replace(/\/+$/, ''),
});
const { data } = await useAsyncGql('product', variables.value);

// Throw 404 if no product data
if (!data.value?.product) throw createError({ statusCode: 404, message: 'Page not found' });

const { $gtm } = useNuxtApp();
const { item } = useDataLayer();

// Custom data
const lightGallery = {
  licenseKey: '3E159A55-D6DA41F8-8FB9729E-B5490B88',
  selector: 'a[href]',
  download: false,
};

// Computed properties
const category = computed(() => [
  data.value?.product?.productTableCategories?.length ? data?.value?.product?.productTableCategories[0] : null,
  data.value?.product?.productChairCategories?.length ? data?.value?.product?.productChairCategories[0] : null,
  data.value?.product?.productClosetCategories?.length ? data?.value?.product?.productClosetCategories[0] : null,
  data.value?.product?.productAccessoryCategories?.length ? data?.value?.product?.productAccessoryCategories[0] : null,
  data.value?.product?.productSofaCategories?.length ? data?.value?.product?.productSofaCategories[0] : null,
  data.value?.product?.productRugCategories?.length ? data?.value?.product?.productRugCategories[0] : null,
  data.value?.product?.productLightingCategories?.length ? data?.value?.product?.productLightingCategories[0] : null,
  data.value?.product?.productOutletCategories?.length ? data?.value?.product?.productOutletCategories[0] : null,
].filter((c) => c)[0] ?? null);

// Watcher
watch(data, (value) => {
  if (value?.product?.seomatic) {
    useSeo(value.product.seomatic);
  }
}, { immediate: true });

// Hooks
onMounted(() => {
  $gtm.trackEvent({ ecommerce: null });
  $gtm.trackEvent({
    event: 'view_item',
    ecommerce: {
      items: [item({ product: data.value?.product })],
    },
  });
});
</script>
